import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import { AppShell } from './AppShell.js';
import { Chat } from './Chat.js';
import { Employees } from './Employees.js';
import { Integrations } from './Integrations.js';
import { AuthCallback } from './AuthCallback.js';
import { NotFound } from './NotFound.js';
import { RouteError } from './RouteError.js';
import { Approvals } from './Approvals.js';

/**
 * Route table for the authenticated app.
 *
 * One screen is still a placeholder: WEB-011 owns Approvals and needs endpoints
 * that do not exist yet. `/chat`, `/employees` and `/integrations` are not -
 * API-008, EMP-005 and INT-006 gave each of them something to render.
 * What this ticket owns is that the routes resolve, that a failure inside one
 * is contained, and that an unknown path is handled rather than blank.
 *
 * `errorElement` is set on the layout rather than per route, so an uncaught
 * error anywhere below renders the shell with an error in it instead of
 * replacing the whole application with a stack trace.
 */
export const routes: RouteObject[] = [
  // Outside the shell on purpose: it runs before there is a session, and the
  // shell's own content depends on having one. `auth-stack.ts` has registered
  // this path since AUTH-002 and nothing served it, so a real login landed on
  // the 404 screen.
  { path: '/auth/callback', element: <AuthCallback />, errorElement: <RouteError /> },
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/chat" replace /> },
      { path: 'chat', element: <Chat /> },
      { path: 'employees', element: <Employees /> },
      { path: 'integrations', element: <Integrations /> },
      // **The last placeholder route.** `Placeholder.tsx` went with it: every
      // screen the navigation offers is now built, and a component that exists
      // to say "not built yet" with nothing to say it about is a thing the next
      // person has to work out is dead.
      { path: 'approvals', element: <Approvals /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];

/** The browser router. Tests build a memory router from the same `routes`. */
export const router = createBrowserRouter(routes);
